import React, { useState, useCallback } from 'react';

export interface FilterOptions {
    categories: string[];
    priceRange: { min: number; max: number };
    condition: ('new' | 'like-new' | 'good' | 'fair')[];
    rating: number | null;
    inStock: boolean;
    onSale: boolean;
    sortBy: 'relevance' | 'price-asc' | 'price-desc' | 'rating' | 'newest';
}

interface AdvancedFiltersProps {
    initialFilters?: Partial<FilterOptions>;
    availableCategories: string[];
    onFiltersChange: (filters: FilterOptions) => void;
    onClose?: () => void;
}

export const AdvancedFilters: React.FC<AdvancedFiltersProps> = ({
    initialFilters,
    availableCategories,
    onFiltersChange,
    onClose
}) => {
    const [filters, setFilters] = useState<FilterOptions>({
        categories: initialFilters?.categories || [],
        priceRange: initialFilters?.priceRange || { min: 0, max: 100000 },
        condition: initialFilters?.condition || [],
        rating: initialFilters?.rating || null,
        inStock: initialFilters?.inStock || false,
        onSale: initialFilters?.onSale || false,
        sortBy: initialFilters?.sortBy || 'relevance'
    });

    const handleCategoryToggle = useCallback((category: string) => {
        setFilters((prev) => ({
            ...prev,
            categories: prev.categories.includes(category)
                ? prev.categories.filter((c) => c !== category)
                : [...prev.categories, category]
        }));
    }, []);

    const handleConditionToggle = useCallback((condition: 'new' | 'like-new' | 'good' | 'fair') => {
        setFilters((prev) => ({
            ...prev,
            condition: prev.condition.includes(condition)
                ? prev.condition.filter((c) => c !== condition)
                : [...prev.condition, condition]
        }));
    }, []);

    const handleApply = useCallback(() => {
        onFiltersChange(filters);
        onClose?.();
    }, [filters, onFiltersChange, onClose]);

    const handleClear = useCallback(() => {
        const cleared: FilterOptions = {
            categories: [],
            priceRange: { min: 0, max: 100000 },
            condition: [],
            rating: null,
            inStock: false,
            onSale: false,
            sortBy: 'relevance'
        };
        setFilters(cleared);
        onFiltersChange(cleared);
        onClose?.();
    }, [onFiltersChange, onClose]);

    const conditionLabels: Record<string, string> = {
        'new': 'Nuevo',
        'like-new': 'Como Nuevo',
        'good': 'Usado - Bueno',
        'fair': 'Usado - Regular'
    };

    return (
        <div className="bg-surface-light dark:bg-surface-dark rounded-xl border border-black/5 dark:border-white/5 overflow-hidden">
            <header className="p-4 border-b border-black/5 dark:border-white/5 flex items-center justify-between">
                <h3 className="font-bold text-lg">Filtros</h3>
                <button onClick={handleClear} className="text-sm text-primary hover:underline">
                    Limpiar todo
                </button>
            </header>

            <div className="p-4 space-y-6 max-h-[60vh] overflow-y-auto custom-scrollbar">
                <div>
                    <h4 className="font-medium mb-3">Ordenar por</h4>
                    <div className="grid grid-cols-2 gap-2">
                        {[
                            { value: 'relevance', label: 'Relevancia' },
                            { value: 'price-asc', label: 'Precio: Menor a Mayor' },
                            { value: 'price-desc', label: 'Precio: Mayor a Menor' },
                            { value: 'rating', label: 'Mejor Valoración' },
                            { value: 'newest', label: 'Más Recientes' }
                        ].map((option) => (
                            <label
                                key={option.value}
                                className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                                    filters.sortBy === option.value
                                        ? 'bg-primary text-white'
                                        : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
                                }`}
                            >
                                <input
                                    type="radio"
                                    name="sortBy"
                                    value={option.value}
                                    checked={filters.sortBy === option.value}
                                    onChange={(e) =>
                                        setFilters((prev) => ({ ...prev, sortBy: e.target.value as FilterOptions['sortBy'] }))
                                    }
                                    className="sr-only"
                                />
                                <span className="text-sm">{option.label}</span>
                            </label>
                        ))}
                    </div>
                </div>

                <div className="h-px bg-black/5 dark:bg-white/5"></div>

                <div>
                    <h4 className="font-medium mb-3">Categorías</h4>
                    <div className="space-y-2">
                        {availableCategories.map((category) => (
                            <label
                                key={category}
                                className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                                    filters.categories.includes(category)
                                        ? 'bg-primary/10 text-primary'
                                        : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                                }`}
                            >
                                <input
                                    type="checkbox"
                                    checked={filters.categories.includes(category)}
                                    onChange={() => handleCategoryToggle(category)}
                                    className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                                />
                                <span className="text-sm">{category}</span>
                            </label>
                        ))}
                    </div>
                </div>

                <div className="h-px bg-black/5 dark:bg-white/5"></div>

                <div>
                    <h4 className="font-medium mb-3">Rango de Precio</h4>
                    <div className="space-y-3">
                        <div className="flex gap-3">
                            <div className="flex-1">
                                <label className="text-xs text-text-secondary">Mínimo</label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary">$</span>
                                    <input
                                        type="number"
                                        value={filters.priceRange.min}
                                        onChange={(e) =>
                                            setFilters((prev) => ({
                                                ...prev,
                                                priceRange: { ...prev.priceRange, min: parseInt(e.target.value) || 0 }
                                            }))
                                        }
                                        className="w-full pl-7 pr-3 py-2 border rounded-lg text-sm"
                                        min={0}
                                    />
                                </div>
                            </div>
                            <div className="flex-1">
                                <label className="text-xs text-text-secondary">Máximo</label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary">$</span>
                                    <input
                                        type="number"
                                        value={filters.priceRange.max}
                                        onChange={(e) =>
                                            setFilters((prev) => ({
                                                ...prev,
                                                priceRange: { ...prev.priceRange, max: parseInt(e.target.value) || 100000 }
                                            }))
                                        }
                                        className="w-full pl-7 pr-3 py-2 border rounded-lg text-sm"
                                        min={0}
                                    />
                                </div>
                            </div>
                        </div>
                        <input
                            type="range"
                            min={0}
                            max={100000}
                            value={filters.priceRange.max}
                            onChange={(e) =>
                                setFilters((prev) => ({
                                    ...prev,
                                    priceRange: { ...prev.priceRange, max: parseInt(e.target.value) }
                                }))
                            }
                            className="w-full accent-primary"
                        />
                    </div>
                </div>

                <div className="h-px bg-black/5 dark:bg-white/5"></div>

                <div>
                    <h4 className="font-medium mb-3">Condición</h4>
                    <div className="flex flex-wrap gap-2">
                        {(['new', 'like-new', 'good', 'fair'] as const).map((condition) => (
                            <button
                                key={condition}
                                onClick={() => handleConditionToggle(condition)}
                                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                                    filters.condition.includes(condition)
                                        ? 'bg-primary text-white'
                                        : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
                                }`}
                            >
                                {conditionLabels[condition]}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="h-px bg-black/5 dark:bg-white/5"></div>

                <div>
                    <h4 className="font-medium mb-3">Valoración</h4>
                    <div className="flex gap-2">
                        {[4, 3, 2, 1].map((rating) => (
                            <button
                                key={rating}
                                onClick={() =>
                                    setFilters((prev) => ({
                                        ...prev,
                                        rating: prev.rating === rating ? null : rating
                                    }))
                                }
                                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                                    filters.rating === rating
                                        ? 'bg-yellow-100 text-yellow-700 border border-yellow-300'
                                        : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
                                }`}
                            >
                                <span className="material-symbols-outlined text-[16px] filled text-yellow-500">star</span>
                                {rating}+
                            </button>
                        ))}
                    </div>
                </div>

                <div className="h-px bg-black/5 dark:bg-white/5"></div>

                <div className="space-y-2">
                    <label className="flex items-center gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={filters.inStock}
                            onChange={(e) => setFilters((prev) => ({ ...prev, inStock: e.target.checked }))}
                            className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
                        />
                        <span className="text-sm font-medium">Solo productos en stock</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={filters.onSale}
                            onChange={(e) => setFilters((prev) => ({ ...prev, onSale: e.target.checked }))}
                            className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
                        />
                        <span className="text-sm font-medium">Solo ofertas</span>
                    </label>
                </div>
            </div>

            <footer className="p-4 border-t border-black/5 dark:border-white/5 flex gap-2">
                <button
                    onClick={handleClear}
                    className="flex-1 py-2 text-sm font-medium text-text-secondary hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                >
                    Cancelar
                </button>
                <button
                    onClick={handleApply}
                    className="flex-1 py-2 bg-primary text-white text-sm font-bold rounded-lg hover:bg-primary/90"
                >
                    Aplicar Filtros
                </button>
            </footer>
        </div>
    );
};
